/**
 * Dev Console → Arche Management → Games (ADR-035).
 *
 * Featured games: the Games page's hero shelf — pick up to twelve games from
 * the catalog, put them in order, and give each an optional kicker ("Editors'
 * Choice"), one line, and wide hero art. Saved as a whole list; the server
 * refuses a pick that is not a game on the catalog.
 *
 * Hidden games: keep a game off the Games page (wrongly flagged as a game, or
 * not up to the page) without touching it in the Store — with a reason, and
 * undoable. Not a takedown: restriction and disable remain the safety tools.
 *
 * Store listings: take an arche's extra Store listing down for a policy
 * breach, with a reason. The slot stays the arche's.
 *
 * And the switch that shows the Games page in this environment at all.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EyeOff, GripVertical, ImagePlus, Loader2, Plus, Save, Search, X } from 'lucide-react';
import type { GameCard } from '@fias/public-site-ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import api, { ApiError, type AdminFeaturedGamePick, type StoreListingWire } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { refreshGamesAvailability } from '@/lib/games-availability';

/** Must match the server (FEATURED_GAMES_MAX and friends in @fias/db-types). */
const FEATURED_MAX = 12;
const LABEL_MAX = 32;
const TAGLINE_MAX = 120;

interface Pick extends AdminFeaturedGamePick {
  /** Stable for drag-and-drop: the arche and, for a listing, the listing. */
  key: string;
  title: string;
  iconUrl: string | null;
  onCatalog: boolean;
  /** The hero art's thumbnail, when the pick has some. */
  heroUrl: string | null;
}

/** Must match the server (GAMES_HERO_ART_* in @fias/db-types). */
const HERO_ART_TYPES = 'image/png,image/jpeg,image/webp';

/** Upload hero art and have the server write it; its key goes on the pick. */
async function uploadHeroArt(file: File): Promise<{ heroKey: string; heroUrlSmall: string }> {
  const { uploadUrl, stagingKey } = await api.adminGetHeroArtUploadUrl({
    mimeType: file.type,
    sizeBytes: file.size,
  });
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed (status ${response.status})`);
  return api.adminWriteHeroArt(stagingKey);
}

function pickKey(archId: string, listingId: string | null | undefined): string {
  return `${archId}:${listingId ?? ''}`;
}

function errorMessage(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong';
}

function SortablePick({
  pick,
  onChange,
  onRemove,
}: {
  pick: Pick;
  onChange: (next: Pick) => void;
  onRemove: () => void;
}): ReactElement {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: pick.key,
  });
  const chooseArt = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const { heroKey, heroUrlSmall } = await uploadHeroArt(file);
      onChange({ ...pick, heroKey, heroUrl: heroUrlSmall });
    } catch (err) {
      toast({
        title: 'Hero art not added',
        description: errorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="rounded-md border bg-card p-2 space-y-2"
    >
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab p-1 text-muted-foreground"
          aria-label={`Move ${pick.title}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        {pick.iconUrl ? (
          <img src={pick.iconUrl} alt="" className="h-7 w-7 rounded-md" />
        ) : (
          <div className="h-7 w-7 rounded-md bg-muted" />
        )}
        <span className="flex-1 truncate text-sm font-medium">{pick.title}</span>
        {!pick.onCatalog && (
          <Badge variant="outline" className="text-xs">
            Not on the Games page
          </Badge>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onRemove}
          aria-label={`Remove ${pick.title}`}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-2 pl-8">
        <Input
          aria-label={`Kicker for ${pick.title}`}
          placeholder="Kicker (e.g. Editors' Choice)"
          maxLength={LABEL_MAX}
          value={pick.label ?? ''}
          onChange={(e) => onChange({ ...pick, label: e.target.value })}
          className="h-8 text-sm"
        />
        <Input
          aria-label={`Tagline for ${pick.title}`}
          placeholder="One line under the title"
          maxLength={TAGLINE_MAX}
          value={pick.tagline ?? ''}
          onChange={(e) => onChange({ ...pick, tagline: e.target.value })}
          className="h-8 text-sm"
        />
      </div>
      <div className="flex items-center gap-2 pl-8">
        {pick.heroUrl ? (
          <img
            src={pick.heroUrl}
            alt={`Hero art for ${pick.title}`}
            className="h-9 w-16 rounded object-cover"
          />
        ) : (
          <span className="text-xs text-muted-foreground">
            No hero art: the card shows the icon
          </span>
        )}
        <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary">
          {uploading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <ImagePlus className="h-3 w-3" />
          )}
          {pick.heroUrl ? 'Replace hero art' : 'Add hero art'}
          <input
            type="file"
            accept={HERO_ART_TYPES}
            className="sr-only"
            aria-label={`Hero art for ${pick.title}`}
            disabled={uploading}
            onChange={(e) => {
              void chooseArt(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {pick.heroUrl && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onChange({ ...pick, heroKey: null, heroUrl: null })}
          >
            Remove art
          </Button>
        )}
      </div>
    </div>
  );
}

function FeaturedGamesCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Pick[] | null>(null);

  const query = useQuery({
    queryKey: queryKeys.games.adminFeatured(),
    queryFn: () => api.adminGetFeaturedGames(),
  });

  const gamesByKey = useMemo(() => {
    const map = new Map<string, GameCard>();
    for (const game of query.data?.games ?? []) {
      map.set(pickKey(game.archId, game.listingId), game);
    }
    return map;
  }, [query.data]);

  const saved: Pick[] = useMemo(
    () =>
      (query.data?.featured ?? []).map((row) => {
        const key = pickKey(row.archId, row.listingId);
        const game = gamesByKey.get(key);
        return {
          key,
          archId: row.archId,
          listingId: row.listingId,
          label: row.label,
          tagline: row.tagline,
          heroKey: row.heroKey,
          heroUrl: row.heroUrl,
          title: row.title ?? game?.title ?? row.archId,
          iconUrl: game?.iconUrl ?? null,
          onCatalog: row.onCatalog,
        };
      }),
    [query.data, gamesByKey],
  );
  const picks = draft ?? saved;
  const pickedKeys = new Set(picks.map((p) => p.key));

  const save = useMutation({
    mutationFn: (list: Pick[]) =>
      api.adminSetFeaturedGames(
        list.map((p) => ({
          archId: p.archId,
          listingId: p.listingId ?? null,
          label: p.label?.trim() || null,
          tagline: p.tagline?.trim() || null,
          heroKey: p.heroKey ?? null,
        })),
      ),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.games.all });
      toast({ title: 'Featured games saved' });
    },
    onError: (err) =>
      toast({ title: 'Not saved', description: errorMessage(err), variant: 'destructive' }),
  });

  const available = (query.data?.games ?? []).filter((game) => {
    const key = pickKey(game.archId, game.listingId);
    if (pickedKeys.has(key)) return false;
    const q = search.trim().toLowerCase();
    return !q || `${game.title} ${game.archeName}`.toLowerCase().includes(q);
  });

  const add = (game: GameCard) => {
    if (picks.length >= FEATURED_MAX) return;
    const listingId = game.listingId;
    setDraft([
      ...picks,
      {
        key: pickKey(game.archId, listingId),
        archId: game.archId,
        listingId,
        label: null,
        tagline: null,
        heroKey: null,
        heroUrl: null,
        title: game.title,
        iconUrl: game.iconUrl,
        onCatalog: true,
      },
    ]);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = picks.findIndex((p) => p.key === active.id);
    const to = picks.findIndex((p) => p.key === over.id);
    setDraft(arrayMove(picks, from, to));
  };

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Featured games didn’t load: {errorMessage(query.error)}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Games on the Games page</CardTitle>
          <CardDescription>Click + to feature a game.</CardDescription>
          <div className="relative mt-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search games..."
              aria-label="Search games"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </CardHeader>
        <CardContent className="max-h-[540px] overflow-y-auto space-y-1">
          {available.map((game) => (
            <div key={game.slug} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted">
              {game.iconUrl ? (
                <img src={game.iconUrl} alt="" className="h-7 w-7 rounded-md" />
              ) : (
                <div className="h-7 w-7 rounded-md bg-muted" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{game.title}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {game.kind === 'listing' ? `in ${game.archeName}` : 'Whole arche'}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => add(game)}
                disabled={picks.length >= FEATURED_MAX}
                aria-label={`Feature ${game.title}`}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {available.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No matching games</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">
                Featured ({picks.length}/{FEATURED_MAX})
              </CardTitle>
              <CardDescription>
                Drag to reorder. With none, the Games page features its top games.
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={() => save.mutate(picks)}
              disabled={draft === null || save.isPending}
              className="gap-1.5"
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </CardHeader>
        <CardContent className="max-h-[540px] overflow-y-auto space-y-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={picks.map((p) => p.key)} strategy={verticalListSortingStrategy}>
              {picks.map((pick) => (
                <SortablePick
                  key={pick.key}
                  pick={pick}
                  onChange={(next) => setDraft(picks.map((p) => (p.key === next.key ? next : p)))}
                  onRemove={() => setDraft(picks.filter((p) => p.key !== pick.key))}
                />
              ))}
            </SortableContext>
          </DndContext>
          {picks.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nothing featured. Add games from the left.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** The most an admin may allow one arche (mirrors GAME_GENRE_ALLOWANCE_MAX). */
const GENRE_ALLOWANCE_CHOICES = [5, 6, 7, 8, 9, 10] as const;

/**
 * Games name up to 4 genres; an admin may allow one arche's games more (ADR-035
 * §1). Nothing already published changes: the arche's next publish uses it.
 */
function MoreGenresCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [archId, setArchId] = useState('');
  const [maxGenres, setMaxGenres] = useState<number>(6);
  const [reason, setReason] = useState('');

  const allowances = useQuery({
    queryKey: queryKeys.games.adminGenreAllowances(),
    queryFn: () => api.adminGetGameGenreAllowances(),
  });

  const change = useMutation({
    mutationFn: (input: Parameters<typeof api.adminSetGameGenreAllowance>[0]) =>
      api.adminSetGameGenreAllowance(input),
    onSuccess: (_result, input) => {
      setArchId('');
      setReason('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.games.adminGenreAllowances() });
      toast({
        title: input.maxGenres === null ? 'Allowance taken away' : 'More genres allowed',
        description: 'It applies from the arche’s next publish or listing change.',
      });
    },
    onError: (err) =>
      toast({ title: 'Not changed', description: errorMessage(err), variant: 'destructive' }),
  });

  const trimmedId = archId.trim();
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">More genres</CardTitle>
        <CardDescription>
          A game names up to 4 genres. Allow one arche’s games more — its own entry and its Store
          listings — up to 10. Nothing already published changes; its next publish uses it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate({ archId: trimmedId, maxGenres, reason: reason.trim() });
          }}
        >
          <Input
            aria-label="Arche to allow more genres"
            placeholder="arc_…"
            maxLength={50}
            value={archId}
            onChange={(e) => setArchId(e.target.value)}
            className="h-9 sm:w-72"
          />
          <select
            aria-label="Genres allowed"
            value={maxGenres}
            onChange={(e) => setMaxGenres(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {GENRE_ALLOWANCE_CHOICES.map((n) => (
              <option key={n} value={n}>
                {n} genres
              </option>
            ))}
          </select>
          <Input
            aria-label="Why this arche may name more genres"
            placeholder="Why (for the audit log)"
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="h-9 flex-1"
          />
          <Button
            type="submit"
            size="sm"
            className="gap-1.5"
            disabled={!trimmedId || !reason.trim() || change.isPending}
          >
            <Plus className="h-4 w-4" />
            Allow
          </Button>
        </form>

        {allowances.isError && (
          <p className="text-sm text-destructive">
            Allowances didn’t load: {errorMessage(allowances.error)}
          </p>
        )}
        {allowances.data && allowances.data.allowances.length === 0 && (
          <p className="text-sm text-muted-foreground">No arche is allowed more than 4 genres.</p>
        )}
        {allowances.data && allowances.data.allowances.length > 0 && (
          <ul className="divide-y rounded-md border">
            {allowances.data.allowances.map((a) => (
              <li key={a.archId} className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {a.archeName}{' '}
                    <span className="font-normal text-muted-foreground">
                      · up to {a.maxGenres} genres · uses {a.genresInUse}
                    </span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{a.reason}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  aria-label={`Take away ${a.archeName}'s allowance`}
                  disabled={change.isPending}
                  onClick={() => change.mutate({ archId: a.archId, maxGenres: null })}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function HiddenGamesCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<GameCard | null>(null);
  const [reason, setReason] = useState('');

  const hidden = useQuery({
    queryKey: queryKeys.games.adminHidden(),
    queryFn: () => api.adminGetHiddenGames(),
  });
  // The catalog as the featured-games picker reads it (one shared request).
  const catalog = useQuery({
    queryKey: queryKeys.games.adminFeatured(),
    queryFn: () => api.adminGetFeaturedGames(),
  });

  const change = useMutation({
    mutationFn: (input: Parameters<typeof api.adminSetGameHidden>[0]) =>
      api.adminSetGameHidden(input),
    onSuccess: (_result, input) => {
      setChosen(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.games.all });
      toast({
        title: input.hidden ? 'Hidden from the Games page' : 'Shown on the Games page again',
        description: 'The Games page updates within a few minutes.',
      });
    },
    onError: (err) =>
      toast({ title: 'Not changed', description: errorMessage(err), variant: 'destructive' }),
  });

  const q = search.trim().toLowerCase();
  const matches = q
    ? (catalog.data?.games ?? [])
        .filter((game) => `${game.title} ${game.archeName}`.toLowerCase().includes(q))
        .slice(0, 8)
    : [];
  const describe = (game: { listingId: string | null; archeName: string }) =>
    game.listingId ? `in ${game.archeName}` : 'Whole arche';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Hidden from the Games page</CardTitle>
        <CardDescription>
          Keeps a game off the Games page without touching it in the Store. To take something down
          everywhere, restrict or disable the arche instead.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Find a game to hide..."
              aria-label="Find a game to hide"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setChosen(null);
              }}
              className="pl-9 h-9"
            />
          </div>
          {!chosen &&
            matches.map((game) => (
              <button
                key={game.slug}
                type="button"
                onClick={() => setChosen(game)}
                className="flex w-full items-center gap-2 rounded-md p-2 text-left hover:bg-muted"
              >
                <span className="flex-1 truncate text-sm font-medium">{game.title}</span>
                <span className="text-xs text-muted-foreground">{describe(game)}</span>
              </button>
            ))}
          {chosen && (
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                change.mutate({
                  archId: chosen.archId,
                  listingId: chosen.listingId,
                  hidden: true,
                  reason: reason.trim(),
                });
              }}
            >
              <span className="self-center text-sm font-medium">{chosen.title}</span>
              <Input
                aria-label={`Why hide ${chosen.title}`}
                placeholder="Why (for the audit log)"
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="h-9 flex-1"
              />
              <Button
                type="submit"
                size="sm"
                className="gap-1.5"
                disabled={!reason.trim() || change.isPending}
              >
                <EyeOff className="h-4 w-4" />
                Hide from Games
              </Button>
            </form>
          )}
        </div>

        {hidden.isError && (
          <p className="text-sm text-destructive">
            Hidden games didn’t load: {errorMessage(hidden.error)}
          </p>
        )}
        {hidden.data && hidden.data.hidden.length === 0 && (
          <p className="text-sm text-muted-foreground">No game is hidden.</p>
        )}
        {hidden.data && hidden.data.hidden.length > 0 && (
          <ul className="divide-y rounded-md border">
            {hidden.data.hidden.map((game) => (
              <li
                key={`${game.archId}:${game.listingId ?? ''}`}
                className="flex items-center gap-3 p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {game.title}{' '}
                    <span className="font-normal text-muted-foreground">({describe(game)})</span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {game.reason} · {new Date(game.hiddenAt).toLocaleDateString()}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={change.isPending}
                  onClick={() =>
                    change.mutate({ archId: game.archId, listingId: game.listingId, hidden: false })
                  }
                >
                  Show again
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function StoreListingsPolicyCard(): ReactElement {
  const { toast } = useToast();
  const [archId, setArchId] = useState('');
  const [loaded, setLoaded] = useState<{ archId: string; listings: StoreListingWire[] } | null>(
    null,
  );
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const id = archId.trim();
    if (!id) return;
    setLoading(true);
    try {
      const { listings } = await api.adminGetArcheStoreListings(id);
      setLoaded({ archId: id, listings });
    } catch (err) {
      toast({
        title: 'Listings didn’t load',
        description: errorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const retire = async (listing: StoreListingWire) => {
    if (!loaded || !reason.trim()) return;
    try {
      const { listing: retired } = await api.adminRetireStoreListing(
        loaded.archId,
        listing.listingId,
        reason.trim(),
      );
      setLoaded({
        ...loaded,
        listings: loaded.listings.map((l) => (l.listingId === retired.listingId ? retired : l)),
      });
      toast({ title: `${listing.title} taken down` });
    } catch (err) {
      toast({ title: 'Not taken down', description: errorMessage(err), variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Store listings</CardTitle>
        <CardDescription>
          Take an extra Store listing down for a policy breach. The arche keeps its slot, so its
          publisher can put up a compliant listing in its place.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void load();
          }}
        >
          <Input
            aria-label="Arche id"
            placeholder="Arche id (arc_…)"
            value={archId}
            onChange={(e) => setArchId(e.target.value)}
            className="h-9"
          />
          <Button type="submit" size="sm" disabled={loading || !archId.trim()}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Show listings'}
          </Button>
        </form>
        {loaded && (
          <>
            <Input
              aria-label="Reason"
              placeholder="Reason (required, kept in the audit log)"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-9"
            />
            {loaded.listings.length === 0 ? (
              <p className="text-sm text-muted-foreground">This arche has no extra listings.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {loaded.listings.map((listing) => (
                  <li key={listing.listingId} className="flex items-center gap-3 p-2">
                    <img src={listing.iconUrl} alt="" className="h-8 w-8 rounded-md" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{listing.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{listing.path}</div>
                    </div>
                    {listing.status === 'retired' ? (
                      <Badge variant="outline">Retired</Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!reason.trim()}
                        onClick={() => void retire(listing)}
                      >
                        Take down
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Whether this environment shows the Games page at all — like Forge's switch:
 * off by default, audited, and the way to run the page on staging while
 * production stays dark.
 */
function GamesPageSwitchCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const setting = useQuery({
    queryKey: queryKeys.games.adminSwitch(),
    queryFn: () => api.adminGetGamesPage(),
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.adminSetGamesPage(enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.games.adminSwitch(), data);
      void refreshGamesAvailability();
      toast({ title: data.enabled ? 'Games page shown' : 'Games page hidden' });
    },
    onError: (err) =>
      toast({
        title: 'Could not change the switch',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Games page</CardTitle>
        <CardDescription>
          Shows or hides the public Games page in this environment — the page, its catalog and
          search, and every Games link. Off until someone turns it on; audited. Other servers follow
          within half a minute, open pages within five.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="games-page-enabled">Show the Games page</Label>
          <Switch
            id="games-page-enabled"
            aria-label="Show the Games page"
            checked={setting.data?.enabled === true}
            disabled={setting.isLoading || setEnabled.isPending}
            onCheckedChange={(checked) => setEnabled.mutate(checked)}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function GamesAdminPanel(): ReactElement {
  return (
    <div className="space-y-6">
      <GamesPageSwitchCard />
      <FeaturedGamesCard />
      <HiddenGamesCard />
      <MoreGenresCard />
      <StoreListingsPolicyCard />
    </div>
  );
}
